import { Component } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Accordion, AccordionPanel, AccordionHeader, AccordionContent } from 'primeng/accordion';
import { DocumentData, DocumentSection } from '../../models/Documentation';
import { environment } from '../../../../../environments/environment';
import { Util } from '../../../../utils/util';

@Component({
  selector: 'app-documentation',
  templateUrl: './documentation.component.html',
  styleUrls: ['./documentation.component.scss'],
  standalone: true,
  imports: [Accordion, AccordionPanel, AccordionHeader, AccordionContent, TranslateModule],
})
export class DocumentationComponent {
  docs: DocumentData[] = [
    {
      title: 'documentation.connexion.title',
      sections: [
        {
          type: 'text',
          content:
            "Pour vous guider dans vos premiers pas sur l'outil, vous pouvez visualiser notre tutoriel dédié:",
          translationKey: 'documentation.connexion.GUIDE_TEXT',
          centered: true,
        },
        this.getCommonLinks().connexionFR,
        this.getCommonLinks().connexionEN,
        this.getCommonLinks().connexionCN,
        {
          type: 'warning',
          content:
            "Attention: Vous devez utiliser l'adresse mail du compte administrateur dans le back office (BOMP).",
          translationKey: 'documentation.connexion.ADMIN_WARNING',
          color: 'blue',
          centered: true,
        },
        {
          type: 'note',
          content:
            'NB: Le vendeur admin de votre boutique peut aussi réinitialiser le mot de passe de votre compte à besoin.',
          translationKey: 'documentation.connexion.RESET_PASSWORD_NOTE',
        },
      ],
      icon: 'pi pi-power-off',
    },
    {
      title: 'documentation.docApi.title',
      sections: [
        {
          type: 'text',
          content:
            'Pour les vendeurs passant par API ou par agrégateur, nous mettons à votre disposition la',
          translationKey: 'documentation.docApi.API_INTRO_TEXT',
        },
        {
          type: 'text',
          content: 'Ce que vous pouvez retrouver dans la documentation :',
          translationKey: 'documentation.docApi.DOC_CONTENT_SUBTITLE',
        },
        {
          type: 'list',
          items: [
            {
              title: 'Description des API',
              titleKey: 'documentation.docApi.API_DESCRIPTION_TITLE',
              content:
                'Toutes les informations nécessaires sur les API, y compris les appels, les formats de données et les réponses attendues',
              contentKey: 'documentation.docApi.API_DESCRIPTION_CONTENT',
              color: 'blue',
            },
            {
              title: 'Endpoints',
              titleKey: 'documentation.docApi.ENDPOINTS_TITLE',
              content:
                "Détails des nouveaux endpoints à utiliser pour l'envoi et la réception des flux",
              contentKey: 'documentation.docApi.ENDPOINTS_CONTENT',
              color: 'blue',
            },
            {
              title: 'Spécifications techniques',
              titleKey: 'documentation.docApi.TECH_SPECS_TITLE',
              content:
                "Les formations de fichier et des méthodes d'authentification pour optimiser les échanges de données avec notre plateforme",
              contentKey: 'documentation.docApi.TECH_SPECS_CONTENT',
              color: 'blue',
            },
          ],
        },
        {
          type: 'text',
          content: 'Retrouvez la documentation API en suivant ce lien :',
          translationKey: 'documentation.docApi.FIND_DOC_TEXT',
        },
        {
          type: 'link',
          content: 'Documentation API',
          translationKey: 'documentation.docApi.DOCUMENTATION_API_LINK',
          url: 'https://catalog-mp.fnacdarty.com/docapi',
          color: 'blue',
        },
      ],
      icon: 'pi pi-book',
    },
    {
      title: 'documentation.profilManagement.title',
      sections: [
        {
          type: 'text',
          content:
            "Dans votre profil en haut à droite de l'écran, vous trouverez les informations relatives à votre compte et également la gestion de votre clé API.",
          translationKey: 'documentation.profilManagement.PROFILE_DESCRIPTION',
        },
        {
          type: 'subtitle',
          content: 'Informations vendeurs',
          translationKey: 'documentation.profilManagement.VENDOR_INFO_SUBTITLE',
          color: 'blue',
        },
        {
          type: 'text',
          content: 'Vous pouvez consulter le tutoriel suivant :',
          translationKey:
            'documentation.profilManagement.VENDOR_INFO_SUBTITLE_CONTENT',
        },
        {
          type: 'link',
          content: 'Consulter et modifier vos informations vendeurs',
          translationKey:
            'documentation.profilManagement.CONSULT_MODIFY_VENDOR_INFO_LINK',
          url: this.getDocUrl()+'Gestion%20profil/FR/Catalog%20-%20FR%20-%20Vendeur%20-%20Consulter%20et%20modifier%20ses%20informations%20profil.pdf',
          color: 'purple',
        },
        {
          type: 'link',
          content: 'Read and modify your profile information',
          translationKey:
            'documentation.profilManagement.MODIFY_PROFILE_EN_LINK',
          url: this.getDocUrl()+'Gestion%20profil/EN/Catalog%20-%20EN%20-%20Seller%20-%20Read%20and%20modify%20your%20profile%20information.pdf',
          color: 'purple',
        },
        {
          type: 'subtitle',
          content: 'Gestion clé API',
          translationKey:
            'documentation.profilManagement.API_KEY_MANAGEMENT_SUBTITLE',
          color: 'blue',
        },
        {
          type: 'text',
          content:
            "L'activation de la clé API permet d'échanger des données entre Fnac-Darty et votre entité. Elle permet de mettre à jour les données de vos catalogues de vos boutiques auxquelles vous êtes rattachés. Une fois générée ou régénérée, elle ne sera plus disponible dans vos paramètres vendeurs. Il faudra donc la stocker dans un endroit sécurisé de votre côté pour la retrouver facilement.",
          translationKey:
            'documentation.profilManagement.API_KEY_MANAGEMENT_SUBTITLE_CONTENT',
        },
        {
          type: 'text',
          content:
            'Vous pourrez la retrouver à tout moment en cas de besoin. Il faudra bien la mettre à jour dans vos appels aux APIs.',
          translationKey:
            'documentation.profilManagement.API_KEY_MANAGEMENT_SUBTITLE_CONTENT_2',
        },
        {
          type: 'text',
          content: 'Consultez le tutoriel dédié:',
          translationKey:
            'documentation.profilManagement.API_KEY_MANAGEMENT_CONSULT_INFO',
        },
        this.getCommonLinks().apiKeyFR,
        this.getCommonLinks().apiKeyEN,
        this.getCommonLinks().apiKeyCN,
      ],
      icon: 'pi pi-user-edit',
    },
    {
      title: 'documentation.shopManagement.title',
      sections: [
        {
          type: 'text',
          content:
            'Dans le menu Boutique vendeur, après avoir filtré sur la boutique, vous avez accès aux paramètres de votre boutique (onglet Gestion boutique) et à la gestion des agrégateurs (onglet Agrégateurs).',
          translationKey:
            'documentation.shopManagement.SHOP_MANAGEMENT_CONTENT',
        },
        {
          type: 'subtitle',
          content: 'Gestion boutique:',
          translationKey:
            'documentation.shopManagement.SHOP_MANAGEMENT_SUBTITLE',
          color: 'blue',
        },
        {
          type: 'text',
          content:
            'Pour mieux comprendre, vous pouvez accéder aux deux tutoriels dédiés:',
          translationKey:
            'documentation.shopManagement.SHOP_MANAGEMENT_TUTORIALS',
        },
        {
          type: 'link',
          content: 'Consulter la sous-page Gestion boutique',
          url: this.getDocUrl()+'Gestion%20boutique/FR/Catalog%20-%20FR%20-%20Vendeur%20-%20Consulter%20la%20sous-page%20gestion%20boutique.pdf',
          color: 'purple',
        },
        {
          type: 'link',
          content: 'Consult the Shop Management sub-page',
          url: this.getDocUrl()+'Gestion%20boutique/EN/Catalog%20-%20EN%20-%20Seller%20-%20Consult%20the%20Shop%20Management%20sub-page.pdf',
          color: 'purple',
        },
        {
          type: 'text',
          content:
            'Les vendeurs admin (uniquement et les vendeurs avec le rôle administrateur) qui accèdent à la gestion des paramètres vendeurs de la boutique et à la Gestion boutique, le peuvent:',
          translationKey:
            'documentation.shopManagement.SHOP_MANAGEMENT_MANAGE_BOUTIQUE_TEXT',
        },
        {
          type: 'link',
          content: 'Créer et activer/désactiver un utilisateur (vendeur admin)',
          url: this.getDocUrl()+'Gestion%20boutique/FR/Catalog%20-%20FR%20-%20Vendeur%20admin%20-%20Cr%C3%A9er%2C%20activer-d%C3%A9sactiver%20un%20utilisateur%20et%20modifier%20ses%20infos.pdf',
          color: 'purple',
        },
        {
          type: 'link',
          content:
            'Create, activate-deactivate user and modify user information (admin seller)',
          url: this.getDocUrl()+'Gestion%20boutique/EN/Catalog%20-%20EN%20-%20Admin%20seller%20-%20Create%2C%20activate-deactivate%20users%20and%20modify%20user%20information.pdf',
          color: 'purple',
        },
        {
          type: 'link',
          content: '中文教程：创建和启用/禁用用户（管理员卖家）',
          url: this.getDocUrl()+'Gestion%20boutique/CN/Catalog%20-%20CN%20-%20Admin%20seller%20-%20Create%2C%20activate-deactivate%20users%20and%20modify%20user%20information.pdf',
          color: 'purple',
        },
        {
          type: 'text',
          content:
            'Pour changer les rôles d’un utilisateur, vous pouvez consulter le tutoriel dédié et effectuer les étapes dans le menu Paramètres > Rôles et droits : ',
          translationKey:
            'documentation.shopManagement.SHOP_MANAGEMENT_MANAGE_ROLES_TEXT',
        },
        this.getCommonLinks().rolesFR,
        this.getCommonLinks().rolesEN,
        this.getCommonLinks().rolesCN,
        {
          type: 'subtitle',
          content: 'Agrégateurs:',
          translationKey: 'documentation.shopManagement.AGGREGATORS_SUBTITLE',
          color: 'blue',
        },
        {
          type: 'text',
          content: 'Pour en savoir plus sur la gestion des agrégateurs:',
          translationKey: 'documentation.shopManagement.AGGREGATORS_TEXT',
        },
        {
          type: 'link',
          content: "Gérer l'association des agrégateurs à la boutique",
          url: this.getDocUrl()+"Gestion%20boutique/FR/Catalog%20-%20FR%20-%20Vendeur%20-%20G%C3%A9rer%20l'association%20des%20agr%C3%A9gateurs%20%C3%A0%20la%20boutique.pdf",
          color: 'purple',
        },
        {
          type: 'link',
          content: 'Manage the association of aggregators to the shop',
          url: this.getDocUrl()+"Gestion%20boutique/EN/Catalog%20-%20EN%20-%20Seller%20-%20Manage%20the%20association%20of%20aggregators%20to%20the%20shop.pdf",
          color: 'purple',
        },
        {
          type: 'link',
          content: '中文教程：管理聚合器与商店的关联',
          url: this.getDocUrl()+"Gestion%20boutique/CN/Catalog%20-%20CN%20-%20Seller%20-%20Manage%20the%20association%20of%20aggregators%20to%20the%20shop.pdf",
          color: 'purple',
        },
      ],
      icon: 'pi pi-shop',
    },
    {
      title: 'documentation.catalogPage.title',
      sections: [
        {
          type: 'text',
          content:
            "Dans le menu Catalogue, dans le sous-menu Catalogue produit, vous pouvez consulter l'ensemble des produits de votre catalogue, en sélectionnant chaque boutique. Vous pouvez effectuer une recherche plus poussée ou libellé/SKU/EAN avec plusieurs critères ou en filtrant de manière approfondie sur le statut, la catégorie, la marque ou encore la date. Il est/sera également possible d'exporter votre sélection de fiches produits.",
          translationKey: 'documentation.catalogPage.catalog_desc',
        },
        {
          type: 'text',
          content:
            'Tous les détails sur la page Catalogue produit dans ce tutoriel:',
          translationKey: 'documentation.catalogPage.tutorial_intro',
        },
        {
          type: 'link',
          content:
            'Consulter et naviguer/filtrer sur la page catalogue produit',
          url: this.getDocUrl()+'Page%20catalogue/FR/Catalog%20-%20FR%20-%20Vendeur%20-%20Consulter%20et%20naviguer-filtrer%20sur%20la%20page%20catalogue%20produit.pdf',
          color: 'purple',
        },
        {
          type: 'link',
          content: 'View and browse-filter on the Product catalog page',
          url: this.getDocUrl()+'Page%20catalogue/EN/Catalog%20-%20EN%20-%20Seller%20-%20View%20and%20browse-filter%20on%20the%20Product%20catalog%20page.pdf',
          color: 'purple',
        },
        {
          type: 'text',
          content:
            'Dans le sous-menu Produits archivés, vous allez retrouver toutes les fiches produits archivées.',
          translationKey: 'documentation.catalogPage.archived_description',
        },
      ],
      icon: 'pi pi-folder-open',
    },
    {
      title: 'documentation.productSheet.title',
      icon: 'pi pi-file',
      sections: [
        {
          type: 'text',
          content:
            "Après avoir accédé à votre Catalogue produit, vous avez en visibilité l'ensemble des fiches produits de votre catalogue. En cliquant sur chacune d'elles, vous avez les détails de la fiche, notamment les statuts des fiches et des attributs. Cela vous permettra de procéder aux modifications éventuelles.",
          translationKey: 'documentation.productSheet.product_description',
        },
        {
          type: 'link',
          content:
            'Consulter une fiche produit, comprendre les statuts de la fiche / attributs et les modifications à apporter',
          url: this.getDocUrl()+'Fiche%20produit/FR/Catalog%20-%20FR%20-%20Vendeur%20-%20Consulter%20une%20fiche%20produit%2C%20comprendre%20les%20statuts%20de%20la%20fiche%2C%20des%20attributs%20et%20les%20modifications%20%C3%A0%20apporter.pdf',
          color: 'purple',
        },
        {
          type: 'link',
          content:
            'Consult a product sheet, understand the status of the sheet/attributes and the modifications to make',
          url: this.getDocUrl()+"Fiche%20produit/EN/Catalog%20-%20EN%20-%20Seller%20-%20Consult%20a%20product%20sheet%2C%20understand%20the%20sheet'%20status%2C%20attributes%20and%20the%20modifications%20to%20make.pdf",
          color: 'purple',
        },
        {
          type: 'link',
          content: '中文教程：创建和启用/禁用用户（管理员专家）',
          url: this.getDocUrl()+"Fiche%20produit/CN/Catalog%20-%20CN%20-%20Seller%20-%20Consult%20a%20product%20sheet%2C%20understand%20the%20sheet'%20status%2C%20attributes%20and%20the%20modifications%20to%20make.pdf",
          color: 'purple',
        },
        {
          type: 'text',
          content:
            'Lorsque vous êtes plusieurs utilisateurs sur une même boutique, vous pouvez consulter toutes les modifications qui ont été apportées sur une fiche produit en suivant ces étapes : Catalogue > Catalogue produit > Cliquer sur une fiche produit > Cliquer sur le bouton "Historique" en haut à droite de l\'écran.',
          translationKey:
            'documentation.productSheet.product_history_description',
        },
     /*    {
          type: 'text',
          content: "Pour plus d'informations, consultez le tutoriel suivant :",
          translationKey:
            'documentation.productSheet.product_history_tutorial_intro',
        },
        {
          type: 'link',
          content:
            "Consulter l'historique des modifications apportées à une fiche produit",
          url: this.getDocUrl()+'',
          color: 'purple',
        },
        {
          type: 'link',
          content: 'View the history of changes made to a product sheet',
          url: this.getDocUrl()+'',
          color: 'purple',
        }, */
      ],
    },
    {
      title: 'documentation.importManagement.title',
      sections: [
        {
          type: 'text',
          content:
            'Dans le menu Imports, vous avez accès à toutes les fonctionnalités relatives à l’import de votre produit.',
          translationKey: 'documentation.importManagement.intro',
        },
        {
          type: 'text',
          content:
            'La première étape est de télécharger votre modèle de fichier d’import dans le sous-menu Modèle de fichier. Il faudra compléter les champs obligatoires du fichier pour l’importer.',
          translationKey: 'documentation.importManagement.steps',
        },
        {
          type: 'text',
          content:
            'Pour en savoir plus sur l’import de catalogue par fichier, vous pouvez vous référer à notre tutoriel dédié : ',
          translationKey: 'documentation.importManagement.tuto',
        },
        {
          type: 'link',
          content: "Générer et compléter un modèle de fichier d'import",
          url: this.getDocUrl()+"Gestion%20des%20imports/FR/Catalog%20-%20FR%20-%20Vendeur%20-%20G%C3%A9n%C3%A9rer%20et%20compl%C3%A9ter%20un%20mod%C3%A8le%20de%20fichier%20d'import.pdf",
          color: 'purple',
        },
        {
          type: 'link',
          content: 'Generate and fill in an import file template',
          url: this.getDocUrl()+"Gestion%20des%20imports/EN/Catalog%20-%20EN%20-%20Seller%20-%20Generate%20and%20fill%20in%20an%20import%20file%20template.pdf",
          color: 'purple',
        },
        {
          type: 'link',
          content: '中文教程：生成并完成导入文件模板',
          url: this.getDocUrl()+'Gestion%20des%20imports/CN/Catalog%20-%20CN%20-%20Seller%20-%20Generate%20and%20fill%20in%20an%20import%20file%20template.pdf',
          color: 'purple',
        },
        {
          type: 'text',
          content:
            'En sélectionnant Import catalogue dans le sous-menu, vous pouvez téléverser votre fichier d’import de catalogue et accédez à l’historique de vos imports. Cette visualisation est clé pour vous permettre de voir le statut de vos imports.',
          translationKey: 'documentation.importManagement.submenu_content',
        },
        {
          type: 'text',
          content: 'Plus d’informations dans le tutoriel suivant : ',
          translationKey: 'documentation.importManagement.tuto_info',
        },
        {
          type: 'link',
          content: 'Importer un fichier et consulter l’historique d’import',
          url: this.getDocUrl()+"Gestion%20des%20imports/FR/Catalog%20-%20FR%20-%20Vendeur%20-%20Importer%20un%20fichier%20et%20consulter%20l'historique%20des%20statuts%20d'import.pdf",
          color: 'purple',
        },
        {
          type: 'link',
          content: 'Import a file and consult the import history',
          url: this.getDocUrl()+"Gestion%20des%20imports/EN/Catalog%20-%20EN%20-%20Seller%20-%20Import%20a%20file%20and%20consult%20the%20import%20files%20status'%20history.pdf",
          color: 'purple',
        },
        {
          type: 'link',
          content: '中文教程：导入文件并查看导入历史记录',
          url: this.getDocUrl()+"Gestion%20des%20imports/CN/Catalog%20-%20CN%20-%20Seller%20-%20Import%20a%20file%20and%20consult%20the%20import%20files%20statuts'%20history.pdf",
          color: 'purple',
        },
        {
          type: 'text',
          content:
            'Une fois votre fichier importé dans Import catalogue, le statut de votre import peut être le suivant : « Besoin mapping : Oui » > « Statut : Mapping en attente ». Si vous importez votre propre format de fichier, hors API, vous devez utiliser le mapping qui rendra votre fichier d’import conforme à la taxonomie Fnac-Darty.',
          translationKey:
            'documentation.importManagement.import_catalog_content',
        },
        {
          type: 'text',
          content:
            "Une fois une catégorie mappée sur un produit, cette catégorie sera automatiquement reprise pour les prochains mappings. Seuls les nouveaux produits seront à traiter lors d'un nouvel import.",
          translationKey:
            'documentation.importManagement.import_catalog_detail',
        },
        {
          type: 'text',
          content: "Pour plus d'informations, consultez le tutoriel suivant : ",
          translationKey: 'documentation.importManagement.import_catalog_tuto',
        },
        {
          type: 'link',
          content: 'Mapper une fiche produit au format Fnac-Darty',
          url: this.getDocUrl()+'Gestion%20des%20imports/FR/Catalog%20-%20FR%20-%20Vendeur%20-%20Mapper%20vos%20fiches%20produits%20au%20format%20FNAC-DARTY.pdf',
          color: 'purple',
        },
        {
          type: 'link',
          content: 'Map a product sheet to the Fnac-Darty format ',
          url: this.getDocUrl()+'Gestion%20des%20imports/CN/Catalog%20-%20CN%20-%20Map%20a%20product%20sheet%20to%20the%20Fnac-Darty%20format.pdf',
          color: 'purple',
        },
        {
          type: 'link',
          content: '使产品详情页符合 FNAC-DARTY 标准格式',
          url: this.getDocUrl()+'Gestion%20des%20imports/EN/Catalog%20-%20EN%20-%20Map%20a%20product%20sheet%20to%20the%20Fnac-Darty%20format.pdf',
          color: 'purple',
        },
        {
          type: 'text',
          content:
            'Après avoir importé votre fichier, vous pouvez accéder au rapport d’erreurs afin de corriger les erreurs dans vos imports. Cela permettra de bien avoir vos fiches produits importées dans l’outil qui, une fois validées, seront visibles en front. ',
          translationKey: 'documentation.importManagement.error_report',
        },
        {
          type: 'link',
          content:
            "Télécharger un rapport d'erreurs et réaliser les correctifs",
          url: this.getDocUrl()+"Gestion%20des%20imports/FR/Catalog%20-%20FR%20-%20Vendeur%20-%20T%C3%A9l%C3%A9charger%20un%20rapport%20d'erreurs%20et%20faire%20les%20correctifs.pdf",
          color: 'purple',
        },
        {
          type: 'link',
          content: 'Download an error report and take corrective action',
          url: this.getDocUrl()+"Gestion%20des%20imports/EN/Catalog%20-%20EN%20-%20Seller%20-%20Download%20an%20error%20report%20and%20take%20corrective%20action(s).pdf",
          color: 'purple',
        },
        {
          type: 'link',
          content: '下载错误报告并进行更正',
          url: this.getDocUrl()+'Gestion%20des%20imports/CN/Catalog%20-%20CN%20-%20Seller%20-%20Download%20an%20error%20report%20and%20take%20corrective%20action(s).pdf',
          color: 'purple',
        },
      ],
      icon: 'pi pi-file-import',
    },
    {
      title: 'documentation.params.title',
      sections: [
        {
          type: 'subtitle',
          content: 'Taxonomie:',
          translationKey: 'documentation.params.taxo_subtitle',
          color: 'blue',
        },
        {
          type: 'text',
          content:
            'Dans les Paramètres, vous avez la possibilité de consulter la taxonomie par catégories et par attributs. Cette compréhension de la taxonomie vous sera utile pour compléter un modèle de fichier.',
          translationKey: 'documentation.params.taxo_content',
        },
        {
          type: 'text',
          content:
            'Pour mieux comprendre et consulter la taxonomie Fnac-Darty, consultez notre tutoriel dédié :',
          translationKey: 'documentation.params.taxo_tuto',
        },
        this.getCommonLinks().taxonomyFR,
        this.getCommonLinks().taxonomyEN,
        this.getCommonLinks().taxonomyCN,
        {
          type: 'subtitle',
          content: 'Rôles et droits:',
          translationKey: 'documentation.params.roles_subtitle',
          color: 'blue',
        },
        {
          type: 'text',
          content:
            'Les vendeurs admin (uniquement les utilisateurs avec le profil administrateurs) ont la possibilité de changer les rôles et les droits des utilisateurs de leur boutique dans le sous-menu Rôles et droits des Paramètres.',
          translationKey: 'documentation.params.roles_content',
        },
        {
          type: 'text',
          content: 'Pour en savoir plus :',
          translationKey: 'documentation.params.roles_tuto',
        },
        this.getCommonLinks().rolesFR,
        this.getCommonLinks().rolesEN,
        this.getCommonLinks().rolesCN,
      ],
      icon: 'pi pi-cog',
    },
    {
      title: 'documentation.help.title',
      sections: [
        {
          type: 'text',
          content:
            "Pour vous accompagner au mieux dans la gestion de votre catalogue, une page d'aide est disponible dans le menu du Portail Catalogue. Cette page centralise toutes les ressources utiles pour vous guider : ",
          translationKey: 'documentation.help.help_content',
        },
        {
          type: 'list',
          items: [
            {
              title: "L'onglet Contact",
              titleKey: 'documentation.help.contact_title',
              content:
                "vous y trouverez la redirection vers l’espace vendeur du back-office et le motif à choisir pour nous écrire en cas de besoin d'assistance spécifique",
              contentKey: 'documentation.help.contact_content',
              color: 'blue',
            },
            {
              title: "L'onglet Documentation",
              titleKey: 'documentation.help.documentation_title',
              content:
                "accédez à l'ensemble des tutoriels vous expliquant les principales fonctionnalités du portail",
              contentKey: 'documentation.help.documentation_content',
              color: 'blue',
            },
            {
              title: "L'onglet FAQ",
              titleKey: 'documentation.help.faq_title',
              content:
                'retrouvez les réponses aux questions les plus fréquentes ',
              contentKey: 'documentation.help.faq_content',
              color: 'blue',
            },
          ],
        },
        {
          type: 'text',
          content:
            'Pour plus d\'informations, consultez le tutoriel suivant :',
          translationKey: 'documentation.help.tuto',
        },
        {
          type: 'link',
          content: 'Accéder au menu Aide (FAQ, Documentation, Contact)',
          url: this.getDocUrl()+'Aide/FR/Catalog%20-%20FR%20-%20Vendeur%20-%20Acc%C3%A9der%20au%20menu%20Aide%20(Contact%2C%20Documentation%2C%20FAQ).pdf',
          color: 'purple',
        },
        {
          type: 'link',
          content: 'Access the Help menu (Q&A, Documentation, Contact)',
          url: this.getDocUrl()+'Aide/EN/Catalog%20-%20EN%20-%20Seller%20-%20Access%20the%20Help%20page%20(Contact%2C%20Documentation%2C%20Q%26A).pdf',
          color: 'purple',
        },
      ],
      icon: 'pi pi-question-circle',
    },
  ];
  constructor() {}



  onDownload(url: string): void {
    if(url == 'https://catalog-mp.fnacdarty.com/docapi') {
      window.open(url, '_blank');
    }else {
      Util.downloadDoc(url)
    }
   
  }

  // Méthodes utilitaires
  getSectionClasses(section: DocumentSection): string {
    const classes = ['document-section'];

    if (section.centered) {
      classes.push('text-center');
    }

    if (section.cssClass) {
      classes.push(section.cssClass);
    }

    return classes.join(' ');
  }

  getColorClass(color?: string): string {
    const colorMap = {
      red: 'text-red-600',
      blue: 'text-blue-600',
      green: 'text-green-600',
      orange: 'text-orange-600',
      purple: 'text-purple-600',
    };

    return color ? colorMap[color as keyof typeof colorMap] || '' : '';
  }

  getDocUrl() {
    return environment.docsBucketUrl;
  }
  getCommonLinks() {
  return {
    // Connexion
    connexionFR: {
      type: 'link' as const,
      content: 'Connexion, présentation du menu et déconnexion',
      translationKey: 'documentation.connexion.CONNEXION_LINK_TEXT',
      url: this.getDocUrl() + 'Connexion/FR/Catalog%20-%20FR%20-%20Vendeur%20-%20Connexion%2C%20pr%C3%A9sentation%20du%20menu%20et%20d%C3%A9connexion.pdf',
      color: 'purple' as const,
    },
    connexionEN: {
      type: 'link' as const,
      content: 'Login, menu presentation and logout',
      translationKey: 'documentation.connexion.LOGIN_LINK_TEXT',
      url: this.getDocUrl() + 'Connexion/EN/Catalog%20-%20EN%20-%20Seller%20-%20Login%2C%20menu%20presentation%20and%20log%20out.pdf',
      color: 'purple' as const,
    },
    connexionCN: {
      type: 'link' as const,
      content: '中文教程：登录、注销和身份验证',
      translationKey: 'documentation.connexion.CHINESE_LINK_TEXT',
      url: this.getDocUrl() + 'Connexion/CN/Catalog%20-%20CN%20-%20Seller%20-%20Login%2C%20menu%20presentation%20and%20log%20out.pdf',
      color: 'purple' as const,
    },

    // API Key Management
    apiKeyFR: {
      type: 'link' as const,
      content: "Activer, générer et régénérer sa clé d'API",
      translationKey: 'documentation.profilManagement.ACTIVATE_GENERATE_API_KEY_LINK',
      url: this.getDocUrl() + 'Gestion%20profil/FR/Catalog%20-%20FR%20-%20Vendeur%20-%20Activer%2C%20g%C3%A9n%C3%A9rer%20et%20reg%C3%A9n%C3%A9rer%20sa%20cl%C3%A9%20API.pdf',
      color: 'purple' as const,
    },
    apiKeyEN: {
      type: 'link' as const,
      content: 'Activate, generate and regenerate your API key',
      translationKey: 'documentation.profilManagement.API_KEY_MANAGEMENT_EN_LINK',
      url: this.getDocUrl() + 'Gestion%20profil/EN/Catalog%20-%20EN%20-%20Seller%20-%20Activate%2C%20generate%20and%20regenerate%20your%20API%20key.pdf',
      color: 'purple' as const,
    },
    apiKeyCN: {
      type: 'link' as const,
      content: '中文教程：激活、生成/重新生成您的 API 密钥',
      translationKey: 'documentation.profilManagement.API_KEY_MANAGEMENT_ZH_LINK',
      url: this.getDocUrl() + 'Gestion%20profil/CN/Catalog%20-%20CN%20-%20Seller%20-%20Activate%2C%20generate%20and%20regenerate%20your%20API%20key.pdf',
      color: 'purple' as const,
    },

    // Roles Management
    rolesFR: {
      type: 'link' as const,
      content: 'Gérer les rôles et les droits d\'un utilisateur (vendeur admin)',
      url: this.getDocUrl() + "Param%C3%A8tres/FR/Catalog%20-%20FR%20-%20Vendeur%20admin%20-%20G%C3%A9rer%20les%20r%C3%B4les%20et%20droits%20d'un%20utilisateur.pdf",
      color: 'purple' as const,
    },
    rolesEN: {
      type: 'link' as const,
      content: "Manage users' roles and rights (admin seller)",
      url: this.getDocUrl() + "Param%C3%A8tres/EN/Catalog%20-%20EN%20-%20Admin%20seller%20-%20Manage%20users'%20roles%20and%20rights.pdf",
      color: 'purple' as const,
    },
    rolesCN: {
      type: 'link' as const,
      content: '管理用户（管理员供应商）的角色和权限',
      url: this.getDocUrl() + "Param%C3%A8tres/CN/Catalog%20-%20CN%20-%20Admin%20seller%20-%20Manage%20users'%20roles%20and%20rights.pdf",
      color: 'purple' as const,
    },

    // Taxonomy
    taxonomyFR: {
      type: 'link' as const,
      content: 'Consulter la taxonomie',
      url: this.getDocUrl() + 'Param%C3%A8tres/FR/Catalog%20-%20FR%20-%20Vendeur%20-%20Consulter%20la%20taxonomie.pdf',
      color: 'purple' as const,
    },
    taxonomyEN: {
      type: 'link' as const,
      content: 'Consult the taxonomy',
      url: this.getDocUrl() + 'Param%C3%A8tres/EN/Catalog%20-%20EN%20-%20Seller%20-%20Consult%20the%20taxonomy.pdf',
      color: 'purple' as const,
    },
    taxonomyCN: {
      type: 'link' as const,
      content: '中文教程：查阅分类',
      url: this.getDocUrl() + 'Param%C3%A8tres/CN/Catalog%20-%20CN%20-%20Seller%20-%20Consult%20the%20taxonomy%20-%20%E5%8D%96%E5%AE%B6%E6%95%99%E7%A8%8B.pdf',
      color: 'purple' as const,
    }
  };
}
}
